import { omit } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UomConversionStatus } from 'companySettings/enums';
import { useBulkSetUpParts } from 'companySettings/hooks';
import { IUomConversionAuditRow } from 'companySettings/types';
import { CorporatePartUpsert } from 'corporate/components/CorporatePartSetUp';
import { UpdateCorporatePartForm } from 'corporate/types';

import { getSelectedPartIds } from '../BulkUpdateUoms/mappers';

interface IProps {
  selectedRows: IUomConversionAuditRow[];
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Applies one set of company part details to every selected part, creating the
 * catalogue entry where the company has none and updating it where it already
 * exists. Uses the same form the part-details panel opens for one part at a
 * time; reusing that component keeps the two paths from drifting, so whatever
 * fields the single-part form grows, this grows with it.
 */
export const BulkSetUpParts = ({ selectedRows, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  const { bulkSetUpParts } = useBulkSetUpParts();

  const partIds = useMemo(() => getSelectedPartIds(selectedRows), [selectedRows]);

  // Sales MOQ and order increment are only needed on the create leg. Forcing them
  // for a selection of parts that all already have an entry would overwrite each
  // of their values with the one typed here, even for someone who opened this to
  // change a different column.
  const areQuantitiesRequired = useMemo(
    () =>
      selectedRows.some((row) => UomConversionStatus.NO_CORPORATE_PART === row.conversionToBase),
    [selectedRows]
  );

  // The form needs a part to label its quantity suffixes with; a selection has no
  // single one, so the fields stay unsuffixed and the drawer says how many parts
  // it covers instead.
  const onSubmitOverride = async (data: UpdateCorporatePartForm): Promise<boolean> => {
    if (0 === partIds.length) {
      return false;
    }

    // `part` and `manufacturerId` are per-part and resolved server-side from the
    // ids, so they are dropped here rather than sent as one part's values.
    const result = await bulkSetUpParts({
      partIds,
      setup: omit(data, ['id', 'part', 'manufacturerId']),
    });

    return Boolean(result?.success);
  };

  return (
    <CorporatePartUpsert
      partNumber=""
      manufacturerId={0}
      isOpen={isOpen}
      onClose={onClose}
      onSubmitOverride={onSubmitOverride}
      title={t('titleBulkSetUpCompanyParts')}
      subtitle={t('messageBulkSetUpPartsSubtitle', { count: partIds.length })}
      areQuantitiesRequired={areQuantitiesRequired}
    />
  );
};
